import { INVOICE_SCENARIO_LABELS, type IncomingInvoice } from "@station/contracts";
import { Decimal } from "@station/core";
import { Alert, Button, MoneyText, SkeletonRows, useToast } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../../features/accounting/company.js";
import { CounterpartyIdCombobox } from "../../../../features/counterparties/counterparty-combobox.js";
import {
  ReasonDialog,
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../../features/counterparties/shared.js";
import { BookingPanel } from "../../../../features/invoicing/booking-panel.js";
import { useIncomingInvoice } from "../../../../features/invoicing/queries.js";
import {
  BookingStatusBadge,
  DaysLeftBadge,
  Field,
  IncomingResponseBadge,
  InvoicingPage,
  Section,
  useInvoicingCrumb,
  useInvoicingPermissions,
  useRefreshInvoicing,
} from "../../../../features/invoicing/shared.js";
import { api } from "../../../../lib/api.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate, formatUnitPrice } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/faturalar/gelen-kutusu/$id")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: IncomingInvoiceDetailPage,
});

function IncomingInvoiceDetailPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const crumb = useInvoicingCrumb("/faturalar/gelen-kutusu");
  const detail = useIncomingInvoice(id);
  const invoice = detail.data;
  const title = invoice ? invoice.invoiceNo : t("menu.invoiceInbox");

  return (
    <InvoicingPage
      title={title}
      {...(invoice ? { description: invoice.senderTitle } : {})}
      breadcrumb={[crumb, { label: title }]}
    >
      {() =>
        detail.error ? (
          <Alert tone="danger">{errorMessage(detail.error)}</Alert>
        ) : !invoice ? (
          <SkeletonRows rows={8} />
        ) : (
          <IncomingBody invoice={invoice} />
        )
      }
    </InvoicingPage>
  );
}

function IncomingBody({ invoice }: { invoice: IncomingInvoice }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInvoicing();
  const permissions = useInvoicingPermissions();
  const [busy, setBusy] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [ignoreOpen, setIgnoreOpen] = useState(false);

  async function run(kind: string, action: () => Promise<void>): Promise<void> {
    setBusy(kind);
    try {
      await action();
    } catch (error) {
      toast({ tone: "danger", title: errorMessage(error) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="grid gap-4">
      {invoice.rejectReason ? (
        <Alert tone="warning" title={t("invoicing.inbox.rejectedTitle")}>
          {invoice.rejectReason}
        </Alert>
      ) : null}

      <Section
        title={t("invoicing.detail.identity")}
        actions={
          invoice.responseStatus === "PENDING" && permissions.canRespondIncoming ? (
            <>
              <Button
                loading={busy === "accept"}
                onClick={() =>
                  void run("accept", async () => {
                    await api.POST("/incoming-invoices/{id}/respond", {
                      params: { path: { id: invoice.id } },
                      body: { action: "ACCEPT", reason: null, version: invoice.version },
                    });
                    await refresh(invoice.companyId);
                    toast({ tone: "success", title: t("invoicing.inbox.accepted") });
                  })
                }
              >
                {t("invoicing.inbox.accept")}
              </Button>
              <Button variant="outline" onClick={() => setRejectOpen(true)}>
                {t("invoicing.inbox.reject")}
              </Button>
            </>
          ) : null
        }
      >
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("common.status")}>
            <span className="flex flex-wrap gap-1.5">
              <IncomingResponseBadge status={invoice.responseStatus} />
              {invoice.responseStatus === "PENDING" && invoice.daysLeft !== null ? (
                <DaysLeftBadge daysLeft={invoice.daysLeft} />
              ) : null}
              <BookingStatusBadge status={invoice.bookingStatus} />
            </span>
          </Field>
          <Field label={t("invoicing.inbox.sender")}>
            <span className="grid">
              {invoice.senderTitle}
              <span className="font-mono text-xs text-muted-foreground">{invoice.senderTaxId}</span>
            </span>
          </Field>
          <Field label={t("invoicing.fields.issueDate")}>
            <span className="tabular">{formatDate(invoice.issueDate)}</span>
          </Field>
          <Field label={t("invoicing.fields.scenario")}>{INVOICE_SCENARIO_LABELS[invoice.scenario]}</Field>
          <Field label={t("invoicing.fields.ettn")}>
            <span className="font-mono text-xs">{invoice.ettn}</span>
          </Field>
          <Field label={t("invoicing.fields.payableAmount")}>
            <MoneyText value={invoice.payableAmount} className="font-semibold" />
          </Field>
          {invoice.responseDueDate ? (
            <Field label={t("invoicing.inbox.responseDueDate")}>
              <span className="tabular">{formatDate(invoice.responseDueDate)}</span>
            </Field>
          ) : null}
          {invoice.bookedInvoiceId ? (
            <Field label={t("invoicing.inbox.bookedInvoice")}>
              <Link
                to="/faturalar/$id"
                params={{ id: invoice.bookedInvoiceId }}
                className="text-primary underline-offset-4 hover:underline"
              >
                {t("invoicing.inbox.openBookedInvoice")}
              </Link>
            </Field>
          ) : null}
        </dl>
      </Section>

      <CounterpartyMatch invoice={invoice} />

      <Section title={t("invoicing.detail.linesTitle")}>
        <SimpleTable
          caption={t("invoicing.detail.linesTitle")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                #
              </th>
              <th scope="col" className={thClass}>
                {t("invoicing.fields.description")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("invoicing.fields.quantity")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("invoicing.fields.unitPrice")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("invoicing.fields.netAmount")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("invoicing.fields.vatAmount")}
              </th>
            </tr>
          }
        >
          {invoice.lines.map((line) => (
            <tr key={line.lineNo} className="border-t">
              <td className={tdClass}>{line.lineNo}</td>
              <td className={tdClass}>
                {line.sellerItemCode ? (
                  <span className="font-mono text-xs text-muted-foreground">{line.sellerItemCode} </span>
                ) : null}
                {line.description}
              </td>
              <td className={tdRightClass}>{Decimal.from(line.quantity).formatTr(3)}</td>
              <td className={tdRightClass}>{formatUnitPrice(line.unitPrice)}</td>
              <td className={tdRightClass}>
                <MoneyText value={line.netAmount} />
              </td>
              <td className={tdRightClass}>
                <MoneyText value={line.vatAmount} />
              </td>
            </tr>
          ))}
        </SimpleTable>
      </Section>

      {permissions.canManagePurchase ? <BookingPanel invoice={invoice} /> : null}

      {invoice.bookingStatus === "PENDING" && permissions.canManagePurchase ? (
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setIgnoreOpen(true)}>
            {t("invoicing.booking.ignore")}
          </Button>
        </div>
      ) : null}

      <ReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title={t("invoicing.inbox.rejectTitle")}
        description={t("invoicing.inbox.rejectDescription")}
        confirmLabel={t("invoicing.inbox.reject")}
        onConfirm={async (reason) => {
          await api.POST("/incoming-invoices/{id}/respond", {
            params: { path: { id: invoice.id } },
            body: { action: "REJECT", reason, version: invoice.version },
          });
          await refresh(invoice.companyId);
          toast({ tone: "success", title: t("invoicing.inbox.rejected") });
        }}
      />
      <ReasonDialog
        open={ignoreOpen}
        onOpenChange={setIgnoreOpen}
        title={t("invoicing.booking.ignoreTitle")}
        description={t("invoicing.booking.ignoreDescription")}
        confirmLabel={t("invoicing.booking.ignore")}
        onConfirm={async (reason) => {
          await api.POST("/incoming-invoices/{id}/ignore", {
            params: { path: { id: invoice.id } },
            body: { reason, version: invoice.version },
          });
          await refresh(invoice.companyId);
          toast({ tone: "success", title: t("invoicing.booking.ignoredDone") });
        }}
      />
    </div>
  );
}

/** Cari eşleştirme: VKN ile cari bulunamadıysa kart açma önerisi, ya da mevcut cariden seçme. */
function CounterpartyMatch({ invoice }: { invoice: IncomingInvoice }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInvoicing();
  const permissions = useInvoicingPermissions();
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const editable = permissions.canManagePurchase;

  async function match(counterpartyId: string | null, kind: string): Promise<void> {
    setBusy(kind);
    try {
      await api.PUT("/incoming-invoices/{id}/counterparty", {
        params: { path: { id: invoice.id } },
        body: { counterpartyId, version: invoice.version },
      });
      await refresh(invoice.companyId);
      toast({ tone: "success", title: t("invoicing.inbox.matched") });
    } catch (error) {
      toast({ tone: "danger", title: errorMessage(error) });
    } finally {
      setBusy(null);
    }
  }

  if (invoice.counterpartyId) {
    return (
      <Section title={t("invoicing.inbox.matchedCounterparty")}>
        <p className="text-sm">
          <Link
            to="/cari/$id"
            params={{ id: invoice.counterpartyId }}
            className="text-primary underline-offset-4 hover:underline"
          >
            {invoice.counterpartyName}
          </Link>
        </p>
      </Section>
    );
  }

  return (
    <Section title={t("invoicing.inbox.matchedCounterparty")}>
      <Alert tone="warning" title={t("invoicing.inbox.noCounterpartyTitle")}>
        {t("invoicing.inbox.noCounterpartyBody", {
          name: invoice.counterpartySuggestion?.name ?? invoice.senderTitle,
          taxId: invoice.counterpartySuggestion?.taxId ?? invoice.senderTaxId,
        })}
      </Alert>
      {editable ? (
        <div className="flex flex-wrap items-end gap-2">
          <Button loading={busy === "create"} onClick={() => void match(null, "create")}>
            {t("invoicing.inbox.createSupplier")}
          </Button>
          <div className="grid gap-1.5">
            <span className="text-sm font-medium">{t("invoicing.inbox.pickExisting")}</span>
            <div className="flex gap-2">
              <CounterpartyIdCombobox
                aria-label={t("invoicing.inbox.pickExisting")}
                companyId={invoice.companyId}
                counterpartyId={selected}
                onChange={setSelected}
              />
              <Button
                variant="outline"
                disabled={!selected}
                loading={busy === "pick"}
                onClick={() => void match(selected, "pick")}
              >
                {t("invoicing.inbox.matchSelected")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </Section>
  );
}
